const WEIGHT_GRAMS_PER_KG = 1000;

class BoxReviewModal extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  dialog = null;

  /** @type {HTMLElement | null} */
  lastTrigger = null;

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    document.addEventListener('click', this.handleDocumentClick);
    this.dialog?.addEventListener('click', this.handleDialogClick);
    this.dialog?.addEventListener('close', this.handleClose);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleDocumentClick);
    this.dialog?.removeEventListener('click', this.handleDialogClick);
    this.dialog?.removeEventListener('close', this.handleClose);
  }

  /** @param {MouseEvent} event */
  handleDocumentClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const openButton = event.target.closest('[data-box-review-open]');
    if (openButton) {
      event.preventDefault();

      if (document.documentElement.dataset.boxReviewPostalMode === 'hidden') {
        window.location.assign(this.dataset.checkoutUrl || '/checkout');
        return;
      }

      this.lastTrigger = openButton instanceof HTMLElement ? openButton : null;
      this.open();
      return;
    }

    if (event.target.closest('[data-box-review-close]')) {
      this.dialog?.close();
    }

    if (event.target.closest('[data-box-review-checkout]')) {
      window.location.assign(this.dataset.checkoutUrl || '/checkout');
    }
  };

  /** @param {MouseEvent} event */
  handleDialogClick = (event) => {
    if (event.target === this.dialog) this.dialog.close();
  };

  handleClose = () => {
    this.lastTrigger?.focus();
  };

  async open() {
    if (!this.dialog) return;

    this.setLoadingState();
    if (!this.dialog.open) this.dialog.showModal();

    try {
      const cart = await this.fetchCart();
      this.renderCart(cart);
    } catch (_error) {
      this.showError();
    }
  }

  async fetchCart() {
    const response = await fetch(this.dataset.cartUrl || '/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    return response.json();
  }

  setLoadingState() {
    const loading = this.querySelector('[data-box-review-loading]');
    const content = this.querySelector('[data-box-review-content]');
    const error = this.querySelector('[data-box-review-error]');
    const checkout = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-box-review-checkout]'));

    if (loading) loading.hidden = false;
    if (content) content.hidden = true;
    if (error) error.hidden = true;
    if (checkout) checkout.disabled = true;
  }

  /** @param {{ total_weight?: number, item_count?: number, items?: Array<{ grams?: number, quantity?: number }> }} cart */
  renderCart(cart) {
    const capacityKg = Math.max(Number(this.dataset.capacityKg) || 40, 1);
    const capacityGrams = capacityKg * WEIGHT_GRAMS_PER_KG;
    const itemWeightGrams = Array.isArray(cart.items)
      ? cart.items.reduce((total, item) => {
          const grams = Math.max(Number(item.grams) || 0, 0);
          const quantity = Math.max(Number(item.quantity) || 0, 0);
          return total + grams * quantity;
        }, 0)
      : 0;
    const totalWeightGrams = Math.max(Number(cart.total_weight) || itemWeightGrams, 0);
    const totalWeightKg = totalWeightGrams / WEIGHT_GRAMS_PER_KG;
    const boxCount = totalWeightGrams > 0 ? Math.ceil(totalWeightGrams / capacityGrams) : 0;

    let currentBoxUsedGrams = totalWeightGrams % capacityGrams;
    if (totalWeightGrams > 0 && currentBoxUsedGrams === 0) currentBoxUsedGrams = capacityGrams;

    const currentBoxUsedKg = currentBoxUsedGrams / WEIGHT_GRAMS_PER_KG;
    const remainingCapacityKg = (capacityGrams - currentBoxUsedGrams) / WEIGHT_GRAMS_PER_KG;
    const utilization = capacityGrams > 0 ? Math.min((currentBoxUsedGrams / capacityGrams) * 100, 100) : 0;
    const currentBox = boxCount;

    this.setText('[data-box-review-box-title]', this.interpolate(this.dataset.boxTitleTemplate, {
      current: currentBox,
      total: boxCount,
    }));
    this.setText('[data-box-review-used]', this.interpolate(this.dataset.usedTemplate, {
      weight: this.formatWeight(currentBoxUsedKg),
    }));
    this.setText('[data-box-review-free]', this.interpolate(this.dataset.freeTemplate, {
      weight: this.formatWeight(remainingCapacityKg),
    }));
    this.setText('[data-box-review-message]', this.interpolate(this.dataset.messageTemplate, {
      weight: this.formatWeight(remainingCapacityKg),
    }));
    this.setText('[data-box-review-boxes]', this.interpolate(this.dataset.boxesTemplate, { count: boxCount }));
    this.setText('[data-box-review-total-weight]', this.interpolate(this.dataset.totalWeightTemplate, {
      weight: this.formatWeight(totalWeightKg),
    }));

    const track = this.querySelector('[data-box-review-track]');
    const progress = /** @type {HTMLElement | null} */ (this.querySelector('[data-box-review-progress]'));
    track?.setAttribute('aria-valuenow', String(Math.round(utilization)));
    if (progress) progress.style.width = `${utilization}%`;

    const loading = this.querySelector('[data-box-review-loading]');
    const content = this.querySelector('[data-box-review-content]');
    const checkout = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-box-review-checkout]'));
    if (loading) loading.hidden = true;
    if (content) content.hidden = false;
    const itemCount = Number(cart.item_count) || cart.items?.reduce((total, item) => total + (Number(item.quantity) || 0), 0);
    if (checkout) checkout.disabled = !itemCount;
  }

  showError() {
    const loading = this.querySelector('[data-box-review-loading]');
    const content = this.querySelector('[data-box-review-content]');
    const error = this.querySelector('[data-box-review-error]');
    if (loading) loading.hidden = true;
    if (content) content.hidden = true;
    if (error) {
      error.textContent = this.dataset.errorText || 'Unable to load the cart. Please try again.';
      error.hidden = false;
    }
  }

  /**
   * @param {string} selector
   * @param {string} value
   */
  setText(selector, value) {
    const element = this.querySelector(selector);
    if (element) element.textContent = value;
  }

  /** @param {string | undefined} template @param {Record<string, string | number>} values */
  interpolate(template = '', values) {
    return Object.entries(values).reduce(
      (result, [key, value]) => result.replaceAll(`[${key}]`, String(value)),
      template
    );
  }

  /** @param {number} weight */
  formatWeight(weight) {
    const locale = document.documentElement.lang || 'en';
    const formatted = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(weight);
    return `${formatted}kg`;
  }
}

if (!customElements.get('box-review-modal')) {
  customElements.define('box-review-modal', BoxReviewModal);
}
